import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import LendingTermsFr from './fr/LendingTermsFr';
import '../styles/Document.css';

function LendingTerms() {
  const { isFr } = useLanguage();
  if (isFr) return <LendingTermsFr />;

  return (
    <div className="doc-page">
      <div className="doc-header">
        <span className="doc-label">Lending</span>
        <h1 className="doc-title">MegaTransact Salary-Based Lending Terms</h1>
        <p className="doc-intro">
          MegaTransact is beginning with salary-based lending as a structured form of
          workplace-linked financial support. These Salary-Based Lending Terms apply in addition to
          the general MegaTransact Terms and Conditions and the MegaTransact Privacy Policy.
        </p>
        <div className="doc-meta">
          <span className="doc-meta-item">
            <strong>Effective Date:</strong> March 27, 2026
          </span>
          <span className="doc-meta-item">
            <strong>Last Updated:</strong> March 27, 2026
          </span>
        </div>
      </div>

      <div className="doc-body">
        <div className="doc-preamble">
          <p>
            These Salary-Based Lending Terms govern your access to and use of MegaTransact's
            salary-based lending service. By applying for, accepting, or using this service, you
            agree to these terms.
          </p>
          <p>
            If there is any inconsistency between these Salary-Based Lending Terms and the general
            MegaTransact Terms and Conditions, these Salary-Based Lending Terms will apply to the
            lending service to the extent of that inconsistency.
          </p>
        </div>

        {/* Section 1 */}
        <div className="doc-section">
          <span className="section-number">Section 1</span>
          <h2 className="section-title">Who We Are</h2>
          <p>
            MegaTransact is a digital platform designed to provide structured salary-linked
            financial support and related digital services.
          </p>
          <p>For questions about these Salary-Based Lending Terms, you may contact us at:</p>
          <div className="doc-contact-box">
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>

        {/* Section 2 */}
        <div className="doc-section">
          <span className="section-number">Section 2</span>
          <h2 className="section-title">Nature of the Service</h2>
          <p>
            MegaTransact's salary-based lending service is a workplace-linked service through which
            eligible users may request short-term financial support subject to review, approval, and
            repayment conditions.
          </p>
          <p>
            This service is structured around salary-linked repayment arrangements and may involve
            employer cooperation, payroll timing, and related workplace processes.
          </p>
          <p>Submitting an application does not guarantee approval.</p>
        </div>

        {/* Section 3 */}
        <div className="doc-section">
          <span className="section-number">Section 3</span>
          <h2 className="section-title">Eligibility</h2>
          <p>
            To apply for salary-based lending through MegaTransact, you must satisfy the
            eligibility criteria applicable at the time of application.
          </p>
          <p>These may include:</p>
          <ul>
            <li>Being of legal age to enter into a binding agreement</li>
            <li>Holding an active account with MegaTransact where required</li>
            <li>Being employed by an approved employer or workplace partner</li>
            <li>Receiving a salary through a structure recognized by MegaTransact</li>
            <li>Providing accurate and complete information and documents</li>
            <li>
              Meeting internal eligibility, operational, risk, or verification requirements
            </li>
          </ul>
          <p>
            MegaTransact may update its eligibility criteria from time to time and may approve or
            decline applications at its discretion, subject to applicable law.
          </p>
        </div>

        {/* Section 4 */}
        <div className="doc-section">
          <span className="section-number">Section 4</span>
          <h2 className="section-title">Applications and Approval</h2>
          <p>When you apply for salary-based lending, MegaTransact may review:</p>
          <ul>
            <li>Your identity information</li>
            <li>Your employment information</li>
            <li>Salary-related details</li>
            <li>Repayment capacity</li>
            <li>Existing obligations on the platform</li>
            <li>Employer-linked or payroll-related information</li>
            <li>Fraud, compliance, or risk indicators</li>
          </ul>
          <p>MegaTransact may:</p>
          <ul>
            <li>Approve the full requested amount</li>
            <li>Approve a lower amount</li>
            <li>Decline the request</li>
            <li>Request additional information before making a decision</li>
            <li>
              Delay or suspend review where verification is incomplete or concerns arise
            </li>
          </ul>
          <p>Approval is not automatic and is never guaranteed.</p>
        </div>

        {/* Section 5 */}
        <div className="doc-section">
          <span className="section-number">Section 5</span>
          <h2 className="section-title">Loan Amount and Duration</h2>
          <p>Any approved amount will be communicated to you before you accept the service.</p>
          <p>For MegaTransact's initial salary-based lending service:</p>
          <ul>
            <li>The maximum loan duration is 2 months</li>
            <li>
              The loan amount may be limited by salary, employer conditions, operational rules,
              risk assessment, or internal policy
            </li>
            <li>
              No user is entitled to any specific amount merely because they applied previously
              or meet basic eligibility criteria
            </li>
          </ul>
          <p>MegaTransact may revise loan size limits and service rules from time to time.</p>
        </div>

        {/* Section 6 */}
        <div className="doc-section">
          <span className="section-number">Section 6</span>
          <h2 className="section-title">Cost of the Service</h2>
          <p>
            Where a salary-based lending request is approved, MegaTransact will disclose the
            applicable repayment amount and repayment structure before you accept.
          </p>
          <p>For MegaTransact's initial model:</p>
          <ul>
            <li>The service applies a charge of 10% per month</li>
            <li>
              For a full 2-month term, this means a total charge of 20% over the approved
              amount, unless a different lawful structure is expressly communicated to the user
              before acceptance
            </li>
          </ul>
          <p>
            You are responsible for reviewing and understanding the amount to be repaid before
            accepting the service.
          </p>
        </div>

        {/* Section 7 */}
        <div className="doc-section">
          <span className="section-number">Section 7</span>
          <h2 className="section-title">Repayment Structure</h2>
          <p>
            Repayment is linked to salary and is expected to take place through agreed repayment
            arrangements, which may include employer-linked deductions or other recognized
            collection methods.
          </p>
          <p>For MegaTransact's initial model:</p>
          <ul>
            <li>Repayment duration must not exceed 2 months</li>
            <li>
              Monthly repayment obligations must remain within the limits applied by
              MegaTransact's internal policy and the relevant employer arrangement
            </li>
            <li>MegaTransact may use employer-supported repayment flows where applicable</li>
            <li>Repayment details will be presented to the user before acceptance</li>
          </ul>
        </div>

        {/* Section 8 */}
        <div className="doc-section">
          <span className="section-number">Section 8</span>
          <h2 className="section-title">Salary-Based Repayment Cap</h2>
          <p>For the initial MegaTransact salary-based lending structure:</p>
          <ul>
            <li>
              The monthly repayment obligation should not exceed 42% of the user's salary under
              MegaTransact's internal lending framework
            </li>
            <li>
              The approved loan amount may also be limited in practice by MegaTransact's policy
              that seeks to keep the total facility within a manageable proportion of the user's
              salary
            </li>
          </ul>
          <p>
            These internal limits are applied for responsible service administration and may be
            adjusted over time.
          </p>
        </div>

        {/* Section 9 */}
        <div className="doc-section">
          <span className="section-number">Section 9</span>
          <h2 className="section-title">Employer Participation</h2>
          <p>Some salary-based lending arrangements depend on employer cooperation.</p>
          <p>Where employer involvement is required, you acknowledge that:</p>
          <ul>
            <li>
              MegaTransact may confirm your employment status and salary-related information
              with the employer
            </li>
            <li>Repayment may depend partly on employer-linked payroll processes</li>
            <li>
              Delays or interruptions may occur if employer-side processes are incomplete, late,
              inaccurate, or unavailable
            </li>
            <li>
              MegaTransact may suspend, delay, or decline service if employer participation is
              not confirmed or maintained
            </li>
          </ul>
          <p>
            MegaTransact is not responsible for employer payroll delays or other workplace
            administrative failures outside its reasonable control.
          </p>
        </div>

        {/* Section 10 */}
        <div className="doc-section">
          <span className="section-number">Section 10</span>
          <h2 className="section-title">Disbursement</h2>
          <p>
            If your application is approved and all required conditions are satisfied,
            MegaTransact may arrange disbursement through one or more supported channels.
          </p>
          <p>Depending on operational setup, this may include:</p>
          <ul>
            <li>Mobile money disbursement</li>
            <li>Approved collection points</li>
            <li>Other approved payout channels introduced over time</li>
          </ul>
          <p>
            Disbursement is considered completed once the approved amount has been released
            through the designated process and recorded by MegaTransact or its payout partner.
          </p>
        </div>

        {/* Section 11 */}
        <div className="doc-section">
          <span className="section-number">Section 11</span>
          <h2 className="section-title">User Responsibilities</h2>
          <p>By using MegaTransact's salary-based lending service, you agree to:</p>
          <ul>
            <li>Provide complete, accurate, and truthful information</li>
            <li>Review the repayment terms carefully before acceptance</li>
            <li>Use the funds for lawful purposes</li>
            <li>
              Ensure you understand the repayment schedule and total amount payable
            </li>
            <li>
              Inform MegaTransact of relevant changes affecting your employment or repayment
              ability where required
            </li>
            <li>Cooperate with verification, repayment, and support processes</li>
          </ul>
        </div>

        {/* Section 12 */}
        <div className="doc-section">
          <span className="section-number">Section 12</span>
          <h2 className="section-title">Missed or Failed Repayments</h2>
          <p>
            If a repayment is missed, delayed, reversed, blocked, or fails for any reason,
            MegaTransact may take appropriate action, including:
          </p>
          <ul>
            <li>Sending reminders or notices</li>
            <li>Restricting access to some or all services</li>
            <li>Contacting the user through approved communication channels</li>
            <li>
              Contacting relevant employer-linked channels where lawful and applicable
            </li>
            <li>Using lawful recovery or collection steps</li>
            <li>Reducing or removing access to future lending requests</li>
          </ul>
          <p>Failure to repay may affect your future eligibility for MegaTransact services.</p>
        </div>

        {/* Section 13 */}
        <div className="doc-section">
          <span className="section-number">Section 13</span>
          <h2 className="section-title">Early Repayment</h2>
          <p>MegaTransact may allow early repayment where operationally possible.</p>
          <p>If early repayment is permitted:</p>
          <ul>
            <li>The repayment process must follow MegaTransact's approved procedures</li>
            <li>
              Any applicable recalculation, if offered, will be communicated clearly
            </li>
            <li>
              Early repayment does not automatically entitle the user to future approval
            </li>
          </ul>
        </div>

        {/* Section 14 */}
        <div className="doc-section">
          <span className="section-number">Section 14</span>
          <h2 className="section-title">Suspension, Decline, or Cancellation</h2>
          <p>
            MegaTransact may suspend, decline, cancel, or reverse access to salary-based lending
            where reasonably necessary, including where:
          </p>
          <ul>
            <li>Eligibility requirements are not met</li>
            <li>Information cannot be verified</li>
            <li>False or misleading information is provided</li>
            <li>Fraud, misuse, or security concerns arise</li>
            <li>Employer participation is unavailable or withdrawn</li>
            <li>Disbursement conditions are not satisfied</li>
            <li>
              Legal, compliance, operational, or risk concerns require intervention
            </li>
          </ul>
          <p>MegaTransact is not obliged to approve or continue any lending request.</p>
        </div>

        {/* Section 15 */}
        <div className="doc-section">
          <span className="section-number">Section 15</span>
          <h2 className="section-title">No Guarantee of Future Access</h2>
          <p>
            Use of MegaTransact's salary-based lending service once does not guarantee future
            approval.
          </p>
          <p>
            Each application may be assessed separately based on current information, service
            rules, employer conditions, prior repayment performance, and operational
            considerations.
          </p>
        </div>

        {/* Section 16 */}
        <div className="doc-section">
          <span className="section-number">Section 16</span>
          <h2 className="section-title">Records and Platform Evidence</h2>
          <p>MegaTransact may maintain records relating to:</p>
          <ul>
            <li>Applications</li>
            <li>Approval decisions</li>
            <li>Accepted terms</li>
            <li>Repayment schedules</li>
            <li>Repayment status</li>
            <li>Employer-linked confirmations</li>
            <li>Support interactions</li>
            <li>Disbursement records</li>
            <li>Collection activity</li>
          </ul>
          <p>
            You agree that MegaTransact's digital records, system logs, and transaction records
            may be used as evidence of service activity, subject to applicable law.
          </p>
        </div>

        {/* Section 17 */}
        <div className="doc-section">
          <span className="section-number">Section 17</span>
          <h2 className="section-title">Privacy and Data Use</h2>
          <p>
            Your use of MegaTransact's salary-based lending service is subject to the MegaTransact
            Privacy Policy.
          </p>
          <p>
            By using this service, you acknowledge that MegaTransact may collect, verify, use,
            store, and share relevant information as reasonably necessary for:
          </p>
          <ul>
            <li>identity verification</li>
            <li>employment confirmation</li>
            <li>salary-linked eligibility assessment</li>
            <li>service administration</li>
            <li>repayment management</li>
            <li>fraud prevention</li>
            <li>compliance and security purposes</li>
          </ul>
        </div>

        {/* Section 18 */}
        <div className="doc-section">
          <span className="section-number">Section 18</span>
          <h2 className="section-title">Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, MegaTransact will not be liable for indirect,
            incidental, consequential, special, or punitive losses arising from:
          </p>
          <ul>
            <li>declined applications</li>
            <li>delayed approval</li>
            <li>employer-side payroll issues</li>
            <li>disbursement delays caused by third parties</li>
            <li>failed communications</li>
            <li>user-provided errors</li>
            <li>service interruptions outside MegaTransact's reasonable control</li>
          </ul>
          <p>
            Nothing in these Salary-Based Lending Terms excludes liability that cannot lawfully
            be excluded.
          </p>
        </div>

        {/* Section 19 */}
        <div className="doc-section">
          <span className="section-number">Section 19</span>
          <h2 className="section-title">Changes to These Terms</h2>
          <p>
            MegaTransact may update these Salary-Based Lending Terms from time to time to reflect
            legal requirements, service changes, product refinement, operational improvements, or
            risk-management adjustments.
          </p>
          <p>
            When material updates are made, MegaTransact may notify users through the app,
            website, email, or other appropriate channels.
          </p>
          <p>
            Continued use of the service after updated terms take effect means you accept the
            revised terms, unless applicable law requires a different process.
          </p>
        </div>

        {/* Section 20 */}
        <div className="doc-section">
          <span className="section-number">Section 20</span>
          <h2 className="section-title">Contact Us</h2>
          <p>
            If you have questions, requests, or concerns about these Salary-Based Lending Terms,
            please contact:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>MegaTransact</strong>
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LendingTerms;
